import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Trash2, AlertTriangle, CheckCircle2, Mail, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatLocalDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function relativeFromNow(iso) {
  if (!iso) return '';
  const target = new Date(iso).getTime();
  const diffSec = Math.round((target - Date.now()) / 1000);
  if (Math.abs(diffSec) < 60) return diffSec >= 0 ? 'in <1 min' : '<1 min ago';
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return diffMin >= 0 ? `in ${diffMin} min` : `${-diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 48) return diffHr >= 0 ? `in ${diffHr} hr` : `${-diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  return diffDay >= 0 ? `in ${diffDay} days` : `${-diffDay} days ago`;
}

/**
 * ScheduledEmailsList — pending + recently-failed scheduled emails.
 *
 * Lists the workspace's queued sends. Admins see everyone's; non-admins see
 * their own only. Lets the user cancel pending sends before they go out.
 */
export default function ScheduledEmailsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchList = async () => {
    try {
      const res = await axios.get(`${API}/communications/scheduled`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(`Failed to load scheduled emails: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    const t = setInterval(fetchList, 30000);
    return () => clearInterval(t);
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled email? The draft will be removed permanently.')) return;
    setCancellingId(id);
    try {
      await axios.delete(`${API}/communications/scheduled/${id}`);
      toast.success('Scheduled email cancelled');
      setItems(items.filter(i => i.id !== id));
    } catch (e) {
      toast.error(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" size={18} />
        Loading scheduled emails…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="scheduled-empty">
        <Clock size={36} className="mx-auto mb-3 text-muted-foreground/50" />
        <p className="font-medium">No scheduled emails</p>
        <p className="text-xs mt-1">When you click "Send later" in Compose, the queue will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="scheduled-list">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">
          {items.filter(i => i.status === 'pending').length} pending
          {items.some(i => i.status === 'failed') && (
            <> · {items.filter(i => i.status === 'failed').length} failed</>
          )}
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchList} className="gap-1.5 h-8">
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>
      <div className="divide-y border rounded-lg overflow-hidden">
        {items.map(item => (
          <div
            key={item.id}
            data-testid={`scheduled-item-${item.id}`}
            className={`p-4 flex items-start gap-3 ${item.status === 'failed' ? 'bg-red-50/50' : 'hover:bg-muted/30'}`}
          >
            <div className={`mt-0.5 ${item.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
              {item.status === 'failed' ? <AlertTriangle size={18} /> : <Clock size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  item.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                }`}>
                  {item.status === 'failed' ? 'FAILED' : 'PENDING'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {item.mailbox === 'personal' ? '· From: my work email' : '· From: shared admin inbox'}
                </span>
              </div>
              <p className="font-medium text-sm mt-1.5 truncate">{item.subject || '(no subject)'}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                <Mail size={11} className="inline mr-1" />
                To: {(item.to || []).join(', ')}
              </p>
              <p className="text-xs mt-1.5">
                <span className="text-muted-foreground">
                  {item.status === 'failed' ? 'Was due ' : 'Sends '}
                </span>
                <span className="font-medium">{formatLocalDateTime(item.send_at)}</span>
                <span className="text-muted-foreground ml-1">({relativeFromNow(item.send_at)})</span>
              </p>
              {item.status === 'failed' && item.last_error && (
                <p className="text-xs text-red-700 mt-1.5 break-words">
                  Reason: {item.last_error}
                </p>
              )}
              {item.attempts > 0 && item.status === 'pending' && (
                <p className="text-xs text-amber-700 mt-1">
                  {item.attempts} retry attempt{item.attempts !== 1 ? 's' : ''} so far
                </p>
              )}
            </div>
            {item.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={cancellingId === item.id}
                onClick={() => handleCancel(item.id)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                data-testid={`scheduled-cancel-${item.id}`}
                title="Cancel scheduled send"
              >
                {cancellingId === item.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </Button>
            )}
            {item.status === 'failed' && (
              <span className="text-xs text-muted-foreground" title="A notification was emailed to the sender">
                <CheckCircle2 size={12} className="inline" /> notified
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
